import { useState, useEffect, useCallback } from 'react';
import { AdminNav } from '../components/AdminNav';
import { useAuth } from '../contexts/AuthContext';
import { PhotoLightbox } from '../components/PhotoLightbox';

interface ResidentRow {
  id: number;
  created_at: string;
  score: number;
  tier: string;
  status: string;
  archived_at: string | null;
  decision_at: string | null;
  decision_by: string | null;
  admin_notes: string | null;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  zip: string;
  county: string;
  cycle_start: string | null;
  cycle_end: string | null;
  cycle_number: number;
  assigned_track: 'mowkin' | 'volunteer' | null;
  user_id: number | null;
  submitter_email: string | null;
  submitter_name: string | null;
  payload: string;
}

interface ProviderRow {
  id: number;
  created_at: string;
  status: string;
  archived_at: string | null;
  decision_at: string | null;
  decision_by: string | null;
  admin_notes: string | null;
  business_name: string;
  owner_name: string;
  phone: string;
  email: string;
  capacity_per_month: string;
  submitter_email?: string | null;
  payload: string;
}

interface HireRow {
  id: number;
  created_at: string;
  status: string;
  archived_at: string | null;
  decision_at: string | null;
  decision_by: string | null;
  admin_notes: string | null;
  contact_name: string;
  phone: string;
  email: string;
  service_address: string;
  city: string;
  zip: string;
  service_type: string;
  frequency: string;
  preferred_guardian: string;
  submitter_email?: string | null;
  payload: string;
}

interface VolunteerRow {
  id: number;
  created_at: string;
  status: 'pending' | 'vetted' | 'rejected' | 'inactive';
  archived_at: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  city: string | null;
  county: string | null;
  vetted_at: string | null;
  vetted_by: string | null;
  notes: string | null;
  submitter_email?: string | null;
  payload: string;
}

interface MowDayRow {
  id: number;
  event_date: string;
  status: 'open' | 'closed' | 'completed' | 'cancelled';
  county: string | null;
  notes: string | null;
  volunteer_count: number;
  resident_count: number;
}

type Tab = 'residents' | 'hires' | 'providers' | 'volunteers' | 'mowdays' | 'calendar';

interface CalendarMowDay {
  id: number;
  event_date: string;
  status: string;
  county: string | null;
  volunteer_count: number;
  resident_count: number;
  volunteers: Array<{ id: number; first_name: string; last_name: string; phone: string; signup_status: string }>;
  residents: Array<{ id: number; first_name: string; last_name: string; phone: string; address: string; city: string; score: number; tier: string }>;
}
interface CalendarData {
  calendar: CalendarMowDay[];
  unassigned: Array<{ id: number; first_name: string; last_name: string; phone: string; address: string; city: string; score: number; tier: string; assigned_track: string | null; cycle_end: string | null }>;
}

function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y) return iso;
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  return dt.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysBetween(a: string, b: string): number {
  return Math.round(
    (new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86400000,
  );
}

function CycleBadge({ end }: { end: string | null }) {
  if (!end) return null;
  const days = daysBetween(todayISO(), end);
  let cls = 'bg-forest/10 text-forest';
  let label = `Active · ${days}d left`;
  if (days < 0) {
    cls = 'bg-terracotta/20 text-terracotta';
    label = `Expired ${Math.abs(days)}d ago`;
  } else if (days <= 14) {
    cls = 'bg-sand text-forest';
    label = `Ending in ${days}d`;
  }
  return (
    <span className={`inline-block px-2 py-1 text-[10px] tracking-[0.1em] uppercase font-medium ${cls}`}>
      {label}
    </span>
  );
}

function TrackBadge({ track }: { track: ResidentRow['assigned_track'] }) {
  if (!track)
    return (
      <span className="inline-block px-2 py-1 text-[10px] tracking-[0.1em] uppercase bg-forest/5 text-forest/60">
        Unassigned
      </span>
    );
  const cls = track === 'mowkin' ? 'bg-forest text-cream' : 'bg-terracotta text-cream';
  return (
    <span className={`inline-block px-2 py-1 text-[10px] tracking-[0.1em] uppercase ${cls}`}>
      {track === 'mowkin' ? 'Mowkin' : 'Volunteer'}
    </span>
  );
}

function PhotoStrip({
  payload,
  onOpen,
}: {
  payload: string;
  onOpen: (keys: string[], index: number) => void;
}) {
  let keys: string[] = [];
  try {
    const p = JSON.parse(payload);
    if (Array.isArray(p?.photo_keys)) {
      keys = p.photo_keys.filter((k: unknown): k is string => typeof k === 'string');
    }
  } catch {
    /* empty */
  }
  if (keys.length === 0)
    return <div className="text-xs italic text-forest/40">No photos uploaded.</div>;
  return (
    <div className="flex gap-2 flex-wrap">
      {keys.map((k, i) => (
        <button
          key={k}
          onClick={() => onOpen(keys, i)}
          className="block border border-forest/20 hover:border-terracotta transition-colors cursor-zoom-in"
          aria-label={`Open photo ${i + 1} of ${keys.length}`}
        >
          <img
            src={`/api/admin/photo/${k}`}
            alt="Resident yard"
            className="w-24 h-24 object-cover"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}

export function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<Tab>('residents');
  const [residents, setResidents] = useState<ResidentRow[]>([]);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [hires, setHires] = useState<HireRow[]>([]);
  const [volunteers, setVolunteers] = useState<VolunteerRow[]>([]);
  const [mowDays, setMowDays] = useState<MowDayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [assignBusy, setAssignBusy] = useState<number | null>(null);
  const [vetBusy, setVetBusy] = useState<number | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ keys: string[]; index: number } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [calendar, setCalendar] = useState<CalendarData | null>(null);

  function openLightbox(keys: string[], index: number) {
    setLightbox({ keys, index });
  }

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = showArchived ? '?archived=1' : '';
      const [r, p, h, v, md, cal] = await Promise.all([
        fetch(`/api/admin/residents${q}`, { credentials: 'include' }),
        fetch(`/api/admin/providers${q}`, { credentials: 'include' }),
        fetch(`/api/admin/hires${q}`, { credentials: 'include' }),
        fetch(`/api/admin/volunteers${q}`, { credentials: 'include' }),
        fetch('/api/admin/mow-days', { credentials: 'include' }),
        fetch('/api/admin/calendar', { credentials: 'include' }),
      ]);
      if ([r, p, h, v, md, cal].some((res) => res.status === 401)) {
        setError('Authentication required. Refresh and enter the admin password.');
        return;
      }
      const [rData, pData, hData, vData, mdData, calData] = await Promise.all([
        r.json(), p.json(), h.json(), v.json(), md.json(), cal.json(),
      ]);
      setResidents(rData.data || []);
      setProviders(pData.data || []);
      setHires(hData.data || []);
      setVolunteers((vData.data && vData.data.volunteers) || []);
      setMowDays((mdData.data && mdData.data.mow_days) || []);
      setCalendar(calData.data || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  async function patchEntity(kind: 'resident' | 'provider' | 'hire' | 'volunteer', id: number, body: object) {
    const tag = `${kind}-${id}`;
    setActionBusy(tag);
    try {
      await fetch(`/api/admin/${kind}/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await loadAll();
    } finally {
      setActionBusy(null);
    }
  }

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  async function assignResidentToMowDay(mowDayId: number, residentId: number) {
    setAssignBusy(residentId);
    try {
      await fetch(`/api/admin/mow-day/${mowDayId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'assign_resident', resident_id: residentId }),
      });
      await loadAll();
    } finally {
      setAssignBusy(null);
    }
  }

  async function patchVolunteer(volunteerId: number, action: 'vet' | 'reject' | 'reactivate' | 'deactivate') {
    setVetBusy(volunteerId);
    try {
      await fetch(`/api/admin/volunteer/${volunteerId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      await loadAll();
    } finally {
      setVetBusy(null);
    }
  }

  async function patchMowDay(id: number, status: MowDayRow['status']) {
    await fetch(`/api/admin/mow-day/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update', status }),
    });
    await loadAll();
  }

  const today = todayISO();
  const expiringSoon = residents.filter((r) => r.cycle_end && daysBetween(today, r.cycle_end) >= 0 && daysBetween(today, r.cycle_end) <= 14).length;
  const expired = residents.filter((r) => r.cycle_end && daysBetween(today, r.cycle_end) < 0).length;
  const pendingVolunteers = volunteers.filter((v) => v.status === 'pending').length;
  const openMowDays = mowDays.filter((m) => m.status === 'open');

  return (
    <>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-terracotta text-sm tracking-[0.25em] uppercase mb-2">Admin</div>
          <h1 className="text-5xl text-forest leading-tight font-display" style={{ fontVariationSettings: '"opsz" 144' }}>
            Submission queue.
          </h1>
        </div>
        {user && (
          <div className="text-right text-sm">
            <div className="text-forest/60 mb-1">{user.email}</div>
            <button onClick={() => void logout()} className="text-xs tracking-[0.15em] uppercase text-forest/70 hover:text-terracotta">
              Sign out
            </button>
          </div>
        )}
      </div>

      {(expiringSoon > 0 || expired > 0 || pendingVolunteers > 0) && (
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          {expired > 0 && (
            <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-sm text-forest">
              <div className="text-xs tracking-[0.15em] uppercase text-terracotta mb-1">Action needed</div>
              <strong>{expired}</strong> resident cycle{expired === 1 ? '' : 's'} expired.
            </div>
          )}
          {expiringSoon > 0 && (
            <div className="bg-sand/40 border-l-4 border-forest p-4 text-sm text-forest">
              <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-1">Upcoming</div>
              <strong>{expiringSoon}</strong> resident cycle{expiringSoon === 1 ? '' : 's'} ending in ≤ 14 days.
            </div>
          )}
          {pendingVolunteers > 0 && (
            <div className="bg-forest/5 border-l-4 border-forest p-4 text-sm text-forest">
              <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-1">Vet</div>
              <strong>{pendingVolunteers}</strong> volunteer application{pendingVolunteers === 1 ? '' : 's'} pending.
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 border-b border-forest/10 flex-wrap flex-1">
          {(['residents', 'hires', 'providers', 'volunteers', 'mowdays', 'calendar'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-sm tracking-[0.1em] uppercase transition-colors ${
                tab === t
                  ? 'bg-forest text-cream'
                  : 'text-forest/70 hover:text-forest hover:bg-forest/5'
              }`}
            >
              {t === 'residents' ? `Residents (${residents.length})`
                : t === 'hires' ? `Paid (${hires.length})`
                : t === 'providers' ? `Pro Mowkins (${providers.length})`
                : t === 'volunteers' ? `Vol. Mowkins (${volunteers.length})`
                : t === 'mowdays' ? `Mow Days (${mowDays.length})`
                : 'Calendar'}
            </button>
          ))}
        </div>
        {(tab === 'residents' || tab === 'providers' || tab === 'volunteers' || tab === 'hires') && (
          <label className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-forest/70 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-terracotta"
            />
            Show archived
          </label>
        )}
      </div>

      {error && (
        <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-forest/60 italic">Loading…</div>
      ) : tab === 'residents' ? (
        <div className="space-y-3">
          {residents.length === 0 && <div className="text-forest/60 italic">No submissions yet.</div>}
          {residents.map((r) => {
            const key = `r-${r.id}`;
            const expanded = expandedId === key;
            return (
              <div key={r.id} className="border border-forest/15 bg-cream">
                <button
                  onClick={() => setExpandedId(expanded ? null : key)}
                  className="w-full p-4 flex items-center gap-4 text-left hover:bg-cream-alt transition-colors"
                >
                  <div className={`shrink-0 w-12 h-12 flex items-center justify-center font-display text-xl ${
                    r.score >= 9 ? 'bg-terracotta text-cream' :
                    r.score >= 5 ? 'bg-forest text-cream' : 'bg-forest/20 text-forest'
                  }`}>
                    {r.score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-forest flex items-center gap-2 flex-wrap">
                      {r.first_name} {r.last_name}
                      <span className="text-forest/50 text-sm">· #{r.id}</span>
                      {r.cycle_number > 1 && (
                        <span className="text-[10px] tracking-[0.1em] uppercase bg-forest/10 text-forest/70 px-2 py-0.5">
                          cycle {r.cycle_number}
                        </span>
                      )}
                      <CycleBadge end={r.cycle_end} />
                      <TrackBadge track={r.assigned_track} />
                    </div>
                    <div className="text-sm text-forest/60 truncate">
                      {r.address}, {r.city}, {r.zip} · {r.phone}
                    </div>
                    {r.submitter_email && (
                      <div className="text-xs text-forest/50 mt-1">
                        Submitted by <span className="text-forest/70 font-medium">{r.submitter_email}</span>
                        {r.submitter_name ? ` (${r.submitter_name})` : ''}
                      </div>
                    )}
                  </div>
                  <div className="text-xs tracking-wider uppercase text-forest/60 hidden sm:block">{r.tier}</div>
                  <div className="text-xs text-forest/40">{new Date(r.created_at).toLocaleDateString()}</div>
                </button>
                {expanded && (
                  <div className="p-6 bg-cream-alt border-t border-forest/10 text-sm space-y-6">
                    {/* Approval / archive actions */}
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-3">Decision</div>
                      <div className="flex gap-2 flex-wrap">
                        {r.status !== 'approved' && (
                          <button onClick={() => patchEntity('resident', r.id, { action: 'approve' })} disabled={actionBusy === `resident-${r.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-terracotta transition-colors disabled:opacity-50">
                            {actionBusy === `resident-${r.id}` ? '…' : '✓ Approve'}
                          </button>
                        )}
                        {r.status !== 'denied' && (
                          <button onClick={() => patchEntity('resident', r.id, { action: 'deny' })} disabled={actionBusy === `resident-${r.id}`} className="text-xs tracking-[0.1em] uppercase bg-terracotta/20 text-terracotta px-4 py-2 hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-50">
                            Deny
                          </button>
                        )}
                        {r.archived_at ? (
                          <button onClick={() => patchEntity('resident', r.id, { action: 'unarchive' })} disabled={actionBusy === `resident-${r.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors disabled:opacity-50">
                            Unarchive
                          </button>
                        ) : (
                          <button onClick={() => patchEntity('resident', r.id, { action: 'archive' })} disabled={actionBusy === `resident-${r.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors disabled:opacity-50">
                            Archive
                          </button>
                        )}
                      </div>
                      {r.decision_at && (
                        <div className="text-xs text-forest/55 mt-2">
                          Decision: {r.status} · {new Date(r.decision_at).toLocaleString()} by {r.decision_by || '—'}
                        </div>
                      )}
                      {r.admin_notes && (
                        <div className="text-xs text-forest/65 mt-2 italic border-l-2 border-forest/20 pl-3">{r.admin_notes}</div>
                      )}
                    </div>

                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-3">Cycle</div>
                      <div className="text-forest/80">
                        {fmtDateLong(r.cycle_start)} → {fmtDateLong(r.cycle_end)} (cycle #{r.cycle_number})
                      </div>
                    </div>
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-3">Photos</div>
                      <PhotoStrip payload={r.payload} onOpen={openLightbox} />
                    </div>
                    {openMowDays.length > 0 && r.assigned_track !== 'volunteer' && (
                      <div>
                        <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-3">Assign to volunteer mow day</div>
                        <div className="flex gap-2 flex-wrap">
                          {openMowDays.map((md) => (
                            <button
                              key={md.id}
                              onClick={() => assignResidentToMowDay(md.id, r.id)}
                              disabled={assignBusy === r.id}
                              className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-3 py-2 hover:bg-terracotta transition-colors disabled:opacity-50"
                            >
                              {assignBusy === r.id ? 'Assigning…' : `→ ${fmtDateLong(md.event_date)}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <details className="text-xs">
                      <summary className="cursor-pointer text-forest/60 tracking-[0.15em] uppercase">Raw payload</summary>
                      <pre className="whitespace-pre-wrap font-mono text-xs text-forest/70 overflow-x-auto mt-2">
                        {JSON.stringify(JSON.parse(r.payload), null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : tab === 'hires' ? (
        <div className="space-y-3">
          {hires.length === 0 && <div className="text-forest/60 italic">No paid requests yet.</div>}
          {hires.map((h) => {
            const key = `h-${h.id}`;
            const expanded = expandedId === key;
            return (
              <div key={h.id} className="border border-forest/15 bg-cream">
                <button onClick={() => setExpandedId(expanded ? null : key)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-cream-alt transition-colors">
                  <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-terracotta text-cream font-display text-xs uppercase">
                    {h.service_type.split('-')[0].slice(0, 4)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-forest flex items-center gap-2 flex-wrap">
                      {h.contact_name} <span className="text-forest/50 text-sm">· #{h.id}</span>
                      <span className="text-[10px] tracking-[0.1em] uppercase bg-forest/10 text-forest/70 px-2 py-0.5">{h.status}</span>
                    </div>
                    <div className="text-sm text-forest/60 truncate">{h.service_type} · {h.service_address}, {h.city}, {h.zip} · {h.phone}</div>
                  </div>
                  <div className="text-xs text-forest/40">{new Date(h.created_at).toLocaleDateString()}</div>
                </button>
                {expanded && (
                  <div className="p-6 bg-cream-alt border-t border-forest/10 text-sm space-y-4">
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-3">Decision</div>
                      <div className="flex gap-2 flex-wrap">
                        {h.status !== 'in_progress' && <button onClick={() => patchEntity('hire', h.id, { action: 'in_progress' })} disabled={actionBusy === `hire-${h.id}`} className="text-xs tracking-[0.1em] uppercase bg-sand text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors disabled:opacity-50">In progress</button>}
                        {h.status !== 'completed' && <button onClick={() => patchEntity('hire', h.id, { action: 'completed' })} disabled={actionBusy === `hire-${h.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-terracotta transition-colors disabled:opacity-50">Mark complete</button>}
                        {h.archived_at ? (
                          <button onClick={() => patchEntity('hire', h.id, { action: 'unarchive' })} disabled={actionBusy === `hire-${h.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream">Unarchive</button>
                        ) : (
                          <button onClick={() => patchEntity('hire', h.id, { action: 'archive' })} disabled={actionBusy === `hire-${h.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream">Archive</button>
                        )}
                      </div>
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-xs text-forest/80 overflow-x-auto">{JSON.stringify(JSON.parse(h.payload), null, 2)}</pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : tab === 'providers' ? (
        <div className="space-y-3">
          {providers.length === 0 && <div className="text-forest/60 italic">No applications yet.</div>}
          {providers.map((p) => {
            const key = `p-${p.id}`;
            const expanded = expandedId === key;
            return (
              <div key={p.id} className="border border-forest/15 bg-cream">
                <button onClick={() => setExpandedId(expanded ? null : key)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-cream-alt transition-colors">
                  <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-forest text-cream font-display">{p.capacity_per_month}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-forest flex items-center gap-2 flex-wrap">
                      {p.business_name} <span className="text-forest/50 text-sm">· #{p.id}</span>
                      <span className="text-[10px] tracking-[0.1em] uppercase bg-forest/10 text-forest/70 px-2 py-0.5">{p.status}</span>
                    </div>
                    <div className="text-sm text-forest/60 truncate">{p.owner_name} · {p.phone} · {p.email}</div>
                  </div>
                  <div className="text-xs text-forest/40">{new Date(p.created_at).toLocaleDateString()}</div>
                </button>
                {expanded && (
                  <div className="p-6 bg-cream-alt border-t border-forest/10 text-sm space-y-4">
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-3">Decision</div>
                      <div className="flex gap-2 flex-wrap">
                        {p.status !== 'approved' && <button onClick={() => patchEntity('provider', p.id, { action: 'approve' })} disabled={actionBusy === `provider-${p.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-terracotta transition-colors disabled:opacity-50">✓ Approve</button>}
                        {p.status !== 'denied' && <button onClick={() => patchEntity('provider', p.id, { action: 'deny' })} disabled={actionBusy === `provider-${p.id}`} className="text-xs tracking-[0.1em] uppercase bg-terracotta/20 text-terracotta px-4 py-2 hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-50">Deny</button>}
                        {p.archived_at ? (
                          <button onClick={() => patchEntity('provider', p.id, { action: 'unarchive' })} disabled={actionBusy === `provider-${p.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream">Unarchive</button>
                        ) : (
                          <button onClick={() => patchEntity('provider', p.id, { action: 'archive' })} disabled={actionBusy === `provider-${p.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream">Archive</button>
                        )}
                      </div>
                      {p.admin_notes && <div className="text-xs text-forest/65 mt-2 italic border-l-2 border-forest/20 pl-3">{p.admin_notes}</div>}
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-xs text-forest/80 overflow-x-auto">{JSON.stringify(JSON.parse(p.payload), null, 2)}</pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : tab === 'volunteers' ? (
        <div className="space-y-3">
          {volunteers.length === 0 && <div className="text-forest/60 italic">No volunteer applications yet.</div>}
          {volunteers.map((v) => {
            const key = `v-${v.id}`;
            const expanded = expandedId === key;
            const statusCls =
              v.status === 'vetted' ? 'bg-forest text-cream' :
              v.status === 'pending' ? 'bg-terracotta text-cream' :
              v.status === 'rejected' ? 'bg-terracotta/20 text-terracotta' :
              'bg-forest/10 text-forest/60';
            return (
              <div key={v.id} className="border border-forest/15 bg-cream">
                <button onClick={() => setExpandedId(expanded ? null : key)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-cream-alt transition-colors">
                  <div className={`shrink-0 w-12 h-12 flex items-center justify-center font-display text-xs uppercase ${statusCls}`}>{v.status.slice(0, 4)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-forest">{v.first_name} {v.last_name} <span className="text-forest/50 text-sm">· #{v.id}</span></div>
                    <div className="text-sm text-forest/60 truncate">{v.city || '—'} ({v.county || '—'}) · {v.phone} · {v.email}</div>
                  </div>
                  <div className="text-xs text-forest/40">{new Date(v.created_at).toLocaleDateString()}</div>
                </button>
                {expanded && (
                  <div className="p-6 bg-cream-alt border-t border-forest/10 text-sm space-y-4">
                    <div className="flex gap-2 flex-wrap">
                      {v.status === 'pending' && (
                        <>
                          <button onClick={() => patchVolunteer(v.id, 'vet')} disabled={vetBusy === v.id} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-terracotta transition-colors disabled:opacity-50">
                            {vetBusy === v.id ? '…' : '✓ Mark vetted'}
                          </button>
                          <button onClick={() => patchVolunteer(v.id, 'reject')} disabled={vetBusy === v.id} className="text-xs tracking-[0.1em] uppercase bg-terracotta/20 text-terracotta px-4 py-2 hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-50">
                            Reject
                          </button>
                        </>
                      )}
                      {v.status === 'vetted' && (
                        <button onClick={() => patchVolunteer(v.id, 'deactivate')} disabled={vetBusy === v.id} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors disabled:opacity-50">
                          Deactivate
                        </button>
                      )}
                      {(v.status === 'rejected' || v.status === 'inactive') && (
                        <button onClick={() => patchVolunteer(v.id, 'reactivate')} disabled={vetBusy === v.id} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-terracotta transition-colors disabled:opacity-50">
                          Reactivate as vetted
                        </button>
                      )}
                      {v.archived_at ? (
                        <button onClick={() => patchEntity('volunteer', v.id, { action: 'unarchive' })} disabled={actionBusy === `volunteer-${v.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream">Unarchive</button>
                      ) : (
                        <button onClick={() => patchEntity('volunteer', v.id, { action: 'archive' })} disabled={actionBusy === `volunteer-${v.id}`} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream">Archive</button>
                      )}
                    </div>
                    {v.vetted_at && (
                      <div className="text-xs text-forest/60">Vetted {new Date(v.vetted_at).toLocaleString()} by {v.vetted_by || '—'}</div>
                    )}
                    <details>
                      <summary className="cursor-pointer text-xs text-forest/60 tracking-[0.15em] uppercase">Application</summary>
                      <pre className="whitespace-pre-wrap font-mono text-xs text-forest/70 overflow-x-auto mt-2">{JSON.stringify(JSON.parse(v.payload), null, 2)}</pre>
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : tab === 'calendar' ? (
        <div className="space-y-6">
          {!calendar && <div className="text-forest/60 italic">Loading calendar...</div>}
          {calendar && calendar.calendar.length === 0 && (
            <div className="text-forest/60 italic">No upcoming mow days.</div>
          )}
          {calendar && calendar.calendar.map((md) => (
            <div key={md.id} className="border border-forest/15 bg-cream p-6">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <div className="font-display text-2xl text-forest">{fmtDateLong(md.event_date)}</div>
                  <div className="text-sm text-forest/60 mt-1">
                    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.1em] uppercase mr-2 ${md.status === 'open' ? 'bg-forest text-cream' : 'bg-forest/10 text-forest/60'}`}>{md.status}</span>
                    {md.county && <span>{md.county} · </span>}
                    {md.volunteer_count} volunteer{md.volunteer_count === 1 ? '' : 's'} · {md.resident_count} resident{md.resident_count === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              {md.volunteers.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-2">Volunteers signed up</div>
                  <ul className="text-sm text-forest/80 space-y-0.5">
                    {md.volunteers.map((v) => (
                      <li key={v.id}>· {v.first_name} {v.last_name} — {v.phone}</li>
                    ))}
                  </ul>
                </div>
              )}
              {md.residents.length > 0 && (
                <div>
                  <div className="text-xs tracking-[0.15em] uppercase text-forest/60 mb-2">Residents assigned</div>
                  <ul className="text-sm text-forest/80 space-y-0.5">
                    {md.residents.map((r) => (
                      <li key={r.id}>· {r.first_name} {r.last_name} — {r.address}, {r.city} (score {r.score})</li>
                    ))}
                  </ul>
                </div>
              )}
              {md.volunteers.length === 0 && md.residents.length === 0 && (
                <div className="text-sm italic text-forest/55">No signups or assignments yet.</div>
              )}
            </div>
          ))}
          {calendar && calendar.unassigned.length > 0 && (
            <div className="border-t-2 border-forest/15 pt-6 mt-8">
              <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Approved · awaiting assignment</div>
              <div className="space-y-2">
                {calendar.unassigned.map((r) => (
                  <div key={r.id} className="bg-cream border border-forest/10 p-3 text-sm">
                    <span className="font-medium text-forest">{r.first_name} {r.last_name}</span>
                    <span className="text-forest/60"> · {r.address}, {r.city}</span>
                    <span className="text-forest/55 text-xs ml-2">(score {r.score} · {r.tier})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {mowDays.length === 0 && <div className="text-forest/60 italic">No mow days scheduled.</div>}
          {mowDays.map((m) => {
            const key = `md-${m.id}`;
            const expanded = expandedId === key;
            const statusCls =
              m.status === 'open' ? 'bg-forest text-cream' :
              m.status === 'closed' ? 'bg-sand text-forest' :
              m.status === 'completed' ? 'bg-terracotta text-cream' :
              'bg-forest/10 text-forest/60';
            return (
              <div key={m.id} className="border border-forest/15 bg-cream">
                <button onClick={() => setExpandedId(expanded ? null : key)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-cream-alt transition-colors">
                  <div className={`shrink-0 w-12 h-12 flex items-center justify-center font-display text-xs uppercase ${statusCls}`}>{m.status.slice(0, 4)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-forest">{fmtDateLong(m.event_date)}</div>
                    <div className="text-sm text-forest/60 truncate">
                      {m.volunteer_count} volunteer{m.volunteer_count === 1 ? '' : 's'} · {m.resident_count} resident{m.resident_count === 1 ? '' : 's'} assigned
                      {m.county ? ` · ${m.county}` : ''}
                    </div>
                  </div>
                </button>
                {expanded && (
                  <div className="p-6 bg-cream-alt border-t border-forest/10 text-sm space-y-3">
                    <div className="flex gap-2 flex-wrap">
                      {m.status !== 'closed' && <button onClick={() => patchMowDay(m.id, 'closed')} className="text-xs tracking-[0.1em] uppercase bg-sand text-forest px-3 py-2 hover:bg-forest hover:text-cream">Close sign-ups</button>}
                      {m.status !== 'open' && <button onClick={() => patchMowDay(m.id, 'open')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-3 py-2 hover:bg-terracotta">Reopen</button>}
                      {m.status !== 'completed' && <button onClick={() => patchMowDay(m.id, 'completed')} className="text-xs tracking-[0.1em] uppercase bg-terracotta text-cream px-3 py-2 hover:bg-forest">Mark completed</button>}
                      {m.status !== 'cancelled' && <button onClick={() => patchMowDay(m.id, 'cancelled')} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-3 py-2 hover:bg-terracotta hover:text-cream">Cancel</button>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Photo lightbox modal */}
      {lightbox && (
        <PhotoLightbox
          photoKeys={lightbox.keys}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onPrev={() =>
            setLightbox((lb) => (lb && lb.index > 0 ? { ...lb, index: lb.index - 1 } : lb))
          }
          onNext={() =>
            setLightbox((lb) =>
              lb && lb.index < lb.keys.length - 1 ? { ...lb, index: lb.index + 1 } : lb,
            )
          }
        />
      )}
    </div>
    </>
  );
}
